if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        // ModalDialog's connectedCallback re-parents this element with
        // document.body.appendChild(this) the first time it runs. Moving an
        // already-connected custom element triggers a *nested*
        // disconnectedCallback + connectedCallback synchronously, so this
        // method can run more than once. Guard our own setup so listeners
        // are only ever attached a single time — otherwise every click
        // fires step() twice and the counter/index get out of sync.
        if (this._controlsSetupDone) return;
        this._controlsSetupDone = true;

        this.container = this.querySelector('[role="document"]');
        this.prevButton = this.querySelector('.product-media-modal__nav-button--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav-button--next');
        this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
        this.counterWrapper = this.querySelector('.product-media-modal__counter');

        // Prevent clicks on the nav controls from bubbling up to the
        // modal's own pointerup handler, which closes the modal on any
        // click that isn't inside a deferred-media/product-model element.
        [this.prevButton, this.nextButton, this.counterWrapper].forEach((el) => {
          if (!el) return;
          el.addEventListener('pointerup', (event) => event.stopPropagation());
          el.addEventListener('click', (event) => event.stopPropagation());
        });

        this.setupLoop();

        if (this.prevButton) {
          this.prevButton.addEventListener('click', () => this.step(-1));
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', () => this.step(1));
        }
        if (this.container) {
          this.container.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
        }
      }

      hide() {
        super.hide();
        this.unlockBodyScroll();
      }

      show(opener) {
        this.lockBodyScroll();
        super.show(opener);
        this.showActiveMedia();
      }

      // iOS Safari has a long-standing bug where `overflow: hidden` on the
      // body alone doesn't reliably stop the page underneath a fixed-position
      // modal from scrolling when a finger drags vertically. Pinning the body
      // in place with position:fixed (and restoring the scroll position on
      // close) is the standard, reliable workaround.
      lockBodyScroll() {
        if (this._scrollLocked) return;
        this._scrollLocked = true;
        this._savedScrollY = window.scrollY || window.pageYOffset || 0;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this._savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
      }

      unlockBodyScroll() {
        if (!this._scrollLocked) return;
        this._scrollLocked = false;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, this._savedScrollY || 0);
      }

      // Real (non-clone) media items, in original order.
      getRealItems() {
        return this.container
          ? Array.from(this.container.querySelectorAll('[data-media-id]:not([data-clone])'))
          : [];
      }

      // Clone the first/last real slides and append/prepend them so that a
      // swipe or button press past either end lands on a visual duplicate,
      // which we then silently (no animation) snap back to the real slide.
      // This is the standard "infinite carousel" technique.
      setupLoop() {
        if (!this.container || this._loopSetupDone) return;
        const realItems = this.getRealItems();
        if (realItems.length < 2) {
          this._loopSetupDone = true;
          return;
        }

        const firstClone = realItems[0].cloneNode(true);
        const lastClone = realItems[realItems.length - 1].cloneNode(true);
        firstClone.setAttribute('data-clone', 'true');
        lastClone.setAttribute('data-clone', 'true');
        firstClone.classList.remove('active');
        lastClone.classList.remove('active');

        this.container.insertBefore(lastClone, this.container.firstChild);
        this.container.appendChild(firstClone);

        this._loopSetupDone = true;
      }

      getAllItems() {
        return this.container ? Array.from(this.container.children) : [];
      }

      showActiveMedia() {
        const realItems = this.getRealItems();
        realItems.forEach((element) => element.classList.remove('active'));

        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]:not([data-clone])`
        );
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        this.currentIndex = realItems.indexOf(activeMedia);
        this.updateCounter();

        requestAnimationFrame(() => {
          if (this.container) {
            this.container.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
          }
        });

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      // Move by +1/-1. If this steps past either end, animate into the
      // matching boundary clone (a single, normal-looking transition), then
      // once the animation settles, silently snap to the real slide with no
      // animation — since the clone is visually identical, nothing appears
      // to jump.
      step(direction) {
        const realItems = this.getRealItems();
        if (!realItems.length || !this.container) return;

        const targetIndex = this.currentIndex + direction;
        let target;

        if (targetIndex < 0) {
          target = this.container.querySelector('[data-clone]:first-child');
          this.currentIndex = realItems.length - 1;
        } else if (targetIndex >= realItems.length) {
          target = this.container.querySelector('[data-clone]:last-child');
          this.currentIndex = 0;
        } else {
          target = realItems[targetIndex];
          this.currentIndex = targetIndex;
        }

        if (!target) return;
        this.updateCounter();
        this.container.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      }

      handleScroll() {
        if (!this.container) return;
        clearTimeout(this._scrollTimeout);
        this._scrollTimeout = setTimeout(() => this.settleScroll(), 120);
      }

      // After any scroll (swipe or programmatic) settles, figure out which
      // slide we actually landed on. If it's a boundary clone — meaning the
      // person swiped past the real last/first slide — silently re-point the
      // scroll position to the matching real slide with no animation.
      settleScroll() {
        if (!this.container) return;
        const allItems = this.getAllItems();
        if (!allItems.length) return;

        const containerLeft = this.container.scrollLeft;
        let closest = null;
        let closestDistance = Infinity;
        allItems.forEach((item) => {
          const distance = Math.abs(item.offsetLeft - containerLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closest = item;
          }
        });
        if (!closest) return;

        const realItems = this.getRealItems();

        if (closest.hasAttribute('data-clone')) {
          const isFirstClone = closest === allItems[allItems.length - 1];
          const target = isFirstClone ? realItems[0] : realItems[realItems.length - 1];
          if (target) {
            this.currentIndex = isFirstClone ? 0 : realItems.length - 1;
            this.container.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
          }
        } else {
          this.currentIndex = realItems.indexOf(closest);
        }
        this.updateCounter();
      }

      updateCounter() {
        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }
    }
  );
}
